<template>
  <div>
    <v-row no-gutters v-if="fieldsLoading">
      <v-col v-for="n in 9" :key="n" cols="12" sm="6" lg="3">
        <v-sheet class="mt-2">
          <v-skeleton-loader class="mx-auto" type="list-item"></v-skeleton-loader>
        </v-sheet>
      </v-col>
    </v-row>
    <template v-else>
      <v-row class="mt-3 mb-2 mx-0">
        <v-col cols="12" class="pa-0 pl-1 pt-1">
          <v-autocomplete outlined dense :label="$t('message.projects.project')" :items="listOfProjects" hide-details
            v-model="activeProjectId" @change="$router.push({ name: 'project_action', params: { project_id: $event } })"
            item-text="projectName" item-value="id"></v-autocomplete>
        </v-col>
      </v-row>
      <v-row class="mb-2 mx-0">
        <form-data :references.sync="formReferences" :model="projectFormModel" ref="projectFormRef"></form-data>
      </v-row>
      <template>
        <v-row>
          <v-col class="pa-1" v-for="(item, index) in speedDialItems" cols="6" lg="2" md="3" sm="4" xl="2" xs="2"
            :key="index">
            <v-card :key="index" :to="item.to" outlined rounded="lg" border variant="outlined" :color="$listSurface">
              <v-card-text class="text-center">
                <template v-if="item.type === 'link'"> <a :href="item.link" target="_blank">{{ item.name
                    }}</a></template>
                <template v-else>{{ item.name }}</template>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>

    <!-- 4975: floating chat launcher, mirroring the web app's bottom-right FAB.
         Rendered only when the backend reports chat enabled for this project —
         the same flag that gates the tile above, so the two can never disagree.
         Sits clear of the app's bottom navigation; see .pcc-fab below. -->
    <v-btn v-if="projectFeaturePwa.project_chat" class="pcc-fab" color="primary" fab dark
      :title="$t('message.projects.chat')" :aria-label="$t('message.projects.chat')"
      @click="openChat">
      <v-badge v-if="chatUnread" color="error" :content="chatUnread" overlap>
        <v-icon>mdi-message-text</v-icon>
      </v-badge>
      <v-icon v-else>mdi-message-text</v-icon>
    </v-btn>

    <!-- Chat opens as an overlay on this page, like the web app's floating
         panel, rather than navigating away. The component is only instantiated
         once opened (v-if on the dialog content), so a project page that never
         opens chat pays nothing for it. -->
    <v-dialog v-model="chatDialog" max-width="900" scrollable
      :fullscreen="$vuetify.breakpoint.smAndDown" transition="dialog-bottom-transition">
      <v-card class="pcc-dialog-card">
        <v-toolbar dense flat dark color="primary" class="flex-grow-0">
          <v-icon small class="mr-2">mdi-message-text</v-icon>
          <v-toolbar-title class="subtitle-2">{{ $t('message.projects.chat') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon small @click="closeChat">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0 pcc-dialog-body">
          <project-chat v-if="chatDialog" :project="activeProjectId" embedded />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FormTemplate from '@/components/FormTemplate'
import { mapGetters } from 'vuex'
import { hostAppApi } from '@/plugins/axios_settings'
import PermissionsMixin from '@/mixins/permissions'
import projectMixin from '@/mixins/projectMixin'
import FeaturesMixin from '@/mixins/features_list'
import WeldMappingMixin from '@/mixins/weldMapping'
import store from '@/store'

export default {
  mixins: [PermissionsMixin, projectMixin, FeaturesMixin, WeldMappingMixin],
  data () {
    return {
      projectEquipmentlist: 0,
      permissionCheckModules: ['drawing', 'ndt', 'pressure-test-report', 'weld-log', 'advanced-weld-log', 'surface-report', 'technical-report', 'material-list', 'pwht', 'pwps', 'wps'], // project feature modules
      listOfProjects: [],
      listOfCustomers: [],
      listOfStates: [],
      activeProjectId: 0,
      showItems: false,
      hostUserId: this.$cookie.get(process.env.VUE_APP_HOST_USER) ? parseInt(this.$cookie.get(process.env.VUE_APP_HOST_USER)) : 0,
      speedDialItems: [],
      customModulesLoading: false,
      fieldsLoading: false,
      customModuleSpeedDialItems: [],
      deviationSpeedDialItem: null,
      clientHeight: window.innerHeight,
      warehouseMaterialSpeedDialItem: null,
      subMenuRightPosition: 1,
      // Per-project PWA visibility for the built-in Hours / Files / Module reports
      // buttons, driven by project_config[*][pwa] (the project setup table's
      // "Visible in mobile app" column) and delivered via get_project_modules_list.
      projectFeaturePwa: { calendar: 0, files: 0, module_report: 0, project_chat: 0 },
      // Unread badge on the floating chat launcher. One cheap COUNT, fetched
      // only when chat is actually enabled for this project.
      chatUnread: 0,
      chatDialog: false
    }
  },
  computed: {
    ...mapGetters(['formType', 'getHostRefApi']),
    ...mapGetters('project', ['project']),
    // The host delivers the dates as YYYY-MM-DD; the fields are read-only, so
    // reformat them for display instead of touching the stored project.
    projectFormModel () {
      const project = this.project
      if (!project) return project
      return Object.assign({}, project)
    },
    formReferences () {
      return {
        cardFlat: true,
        cardTitleClass: 'pa-1',
        layoutClass: 'pa-1',
        properties: [{
          // The host aliases its columns on the way out: `number` is po_ranked
          // (the web's "Project no."), `name` is ao_ranked and `po` is the
          // actual project_name. Bound by those alias names, labelled by what
          // they really are, so the PWA reads the same as the web project tab.
          model: 'number',
          type: this.formType.TEXT,
          rules: [],
          disabled: true,
          label: this.$t('message.projects.projectNo'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'name',
          type: this.formType.TEXT,
          rules: [],
          disabled: true,
          label: this.$t('message.projects.aoRanked'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          // po_name/ao_name, not po/ao: the detail endpoint renames them on the
          // way out (GetAction::transform) even though the host aliases them as
          // po/ao. Binding the host's names leaves both boxes blank.
          model: 'po_name',
          type: this.formType.TEXT,
          rules: [],
          disabled: true,
          label: this.$t('message.projects.projectname'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'ao_name',
          type: this.formType.TEXT,
          rules: [],
          disabled: true,
          label: this.$t('message.projects.workOrderName'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'start_date',
          type: this.formType.TEXT,
          rules: [],
          disabled: true,
          label: this.$t('message.projects.startDate'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'end_date',
          type: this.formType.TEXT,
          rules: [],
          disabled: true,
          label: this.$t('message.projects.endDate'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'customer_id',
          type: this.formType.AUTO_COMPLETE,
          rules: [],
          items: this.listOfCustomers,
          select_text: 'name',
          select_value: 'id',
          disabled: true,
          label: this.$t('message.projects.customer'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'state_id',
          type: this.formType.AUTO_COMPLETE,
          rules: [],
          items: this.listOfStates,
          select_text: this.$i18n.locale === 'en' ? 'en_name' : 'no_name',
          select_value: 'id',
          disabled: true,
          label: this.$t('message.projects.status'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'description',
          type: this.formType.TEXTAREA,
          rules: [],
          label: this.$t('message.deviation.description'),
          class: 'xs12',
          rows: 3,
          disabled: true
        }],
        buttons: [{
          name: 'back',
          color: 'info',
          click: () => this.$router.go(-1), // this.$router.push('/projects'),
          label: this.$t('message.login.back'),
          to: '/projects',
          is_show: true
        }]
      }
    },
    customStyle () {
      return 'position:fixed;'
    }
  },
  created () {
    this.getAllProjects()
    this.getCustomers()
    this.getStates()
    if (this.$route.params.project_id) {
      this.activeProjectId = parseInt(this.$route.params.project_id)
    }

    /* On Resize */
    window.addEventListener('resize', this.windowResizeHandler)
  },
  mounted () {
    this.getCustomModules()
    this.$eventBus.$on('languageChanged', (locale) => {
      this.getCustomModules()
    })
    this.getSubMenuItemRightPosition()
    // this.setPermission()
  },
  methods: {
    setSpeedDialItems () {
      // default speed dial items
      this.speedDialItems = []
      const calendarHours = {
        name: this.$t('message.timer.hours'),
        icon: 'mdi-plus',
        to: `/calendar/actions?project_id=${this.activeProjectId}`
      }
      const projectFiles = {
        name: this.$t('message.layout.files'),
        icon: 'mdi-file',
        to: `/files?project_id:${this.activeProjectId}`
      }
      if (this.projectFeaturePwa.calendar) this.speedDialItems.push(calendarHours)
      if (this.projectFeaturePwa.files) this.speedDialItems.push(projectFiles)

      /* add speed dial item dynamic values */
      const projectCheckList = {
        name: this.$t('message.checklist.title'),
        icon: 'mdi-book-plus',
        to: `/projects/${this.activeProjectId}/checklists`
      }
      const projectEquipment = {
        name: this.$t('message.equipments.equipment'),
        icon: 'mdi-book-plus',
        to: `/projects/${this.activeProjectId}/equipments`
      }
      this.speedDialItems.push(projectCheckList) // if (this.checkFeatureEnabled('checklists')) this.speedDialItems.push(projectCheckList)
      if (this.checkFeatureEnabled('equipments') && this.checkActionPermission(this.CONSTANTS.projectequipment, this.CONSTANTS.LIST) && this.projectEquipmentlist) this.speedDialItems.push(projectEquipment)
      // check if deviation speeddialItem has then add
      if (this.deviationSpeedDialItem && this.checkFeatureEnabled('deviations')) this.speedDialItems.push(this.deviationSpeedDialItem)
      // check permission if has custom modules list permission then show add in the menu list
      if (!this.checkActionPermission(this.CONSTANTS.custommodule, this.CONSTANTS.LIST)) this.customModuleSpeedDialItems = this.customModuleSpeedDialItems.filter(x => x.type !== 'module')
      this.speedDialItems.push.apply(this.speedDialItems, this.customModuleSpeedDialItems)
      // if (this.checkActionPermission(this.CONSTANTS.custommodule, this.CONSTANTS.LIST)) this.speedDialItems.push.apply(this.speedDialItems, this.customModuleSpeedDialItems)
      // module report speed dial
      const moduleReport = {
        name: this.$t('message.moduleReports.title'),
        icon: 'mdi-book-plus',
        to: `/projects/${this.activeProjectId}/module_report`
      }
      if (this.projectFeaturePwa.module_report) this.speedDialItems.push(moduleReport)
      // 4975 — internal project chat is deliberately NOT a tile here. It is
      // reached from the floating launcher (.pcc-fab in the template), matching
      // the web app, where chat is a persistent button rather than one module
      // card among many. The projectFeaturePwa.project_chat flag still gates
      // that launcher.
      if (this.warehouseMaterialSpeedDialItem && this.checkFeatureEnabled('warehouse')) this.speedDialItems.push(this.warehouseMaterialSpeedDialItem)
    },
    getAllProjects () {
      this.getSetListItems(`projects/get_as_simplified_for_pwa?filter=${this.isAdmin() ? 3 : 1}`, 'listOfProjects') // 4 is all, 2 is myall
    },
    getCustomers () {
      this.getSetListItems('customers', 'listOfCustomers')
    },
    getStates () {
      this.getSetListItems('projectstates', 'listOfStates')
    },
    getSetListItems (url, listName) {
      this.$api.execute('get', url).then((response) => {
        if (response.data) {
          if (listName === 'listOfProjects') {
            const list = this.$formatter.cloneVariable(response.data)
            list.forEach(project => {
              project.projectName = this.setProjectname(project)
            })
            this[listName] = list
          } else this[listName] = response.data
        }
      })
    },
    getCustomModules () {
      this.customModulesLoading = true
      this.deviationSpeedDialItem = null
      this.warehouseMaterialSpeedDialItem = null
      this.projectFeaturePwa = { calendar: 0, files: 0, module_report: 0, project_chat: 0 }
      hostAppApi.get(`${this.getHostRefApi}get_project_modules_list?project_id=${this.activeProjectId}&user_id=${this.hostUserId}`, { headers: { language: this.$i18n.locale } })
        .then((response) => {
          if (response.data && response.data.length > 0) {
            const list = this.$formatter.cloneVariable(response.data)
            const customModules = []
            list.map((customModule) => {
              // Built-in Hours / Files / Module reports tiles: capture their
              // per-project PWA visibility flag; they are rendered separately in
              // setSpeedDialItems(), not as custom modules.
              if (['calendar', 'files', 'module_report', 'project_chat'].includes(customModule.tab)) {
                this.projectFeaturePwa[customModule.tab] = Number(customModule.enabled) ? 1 : 0
                return
              }
              if (customModule.tab === 'equipment') {
                this.projectEquipmentlist = 1
              }
              const obj = { name: customModule.type === 'link' ? 'SharePoint' : customModule.displayName }
              if (customModule.type === 'feature') {
                if (customModule.tab === 'deviation' && this.checkActionPermission(this.CONSTANTS.projectdeviation, this.CONSTANTS.LIST)) {
                  obj.to = `/projects/${this.activeProjectId}/deviations`
                  this.deviationSpeedDialItem = obj
                } else if (customModule.tab === 'warehouse-material') {
                  obj.to = `/projects/${this.activeProjectId}/materials`
                  this.warehouseMaterialSpeedDialItem = obj
                } else if (customModule.type === 'feature' && this.permissionCheckModules.includes(customModule.tab) && this.checkActionPermission(this.CONSTANTS[customModule.tab], this.CONSTANTS.LIST)) {
                  obj.to = `/projects/${this.activeProjectId}/${customModule.tab}`
                  // Weld Mapping entry point. `weld_mapping` on this entry is the
                  // backend's own verdict (org feature AND tenant AND project
                  // advanced_weldlog); combined with the user-level permission it
                  // reproduces the web gate without restating it here. When it is
                  // off, Advanced Weld Log keeps its existing destination.
                  if (customModule.tab === 'advanced-weld-log' && Number(customModule.weld_mapping) && this.weldMappingEnabled) {
                    obj.to = `/projects/${this.activeProjectId}/advanced-weld-log/weld-mapping`
                  }
                  customModules.push(obj)
                }
              } else if (customModule.type === 'module') {
                obj.to = `/projects/${this.activeProjectId}/custom_modules/${customModule.id}?name=${customModule.displayName || customModule.name || ''}`
                customModules.push(obj)
              } else if (customModule.type === 'link') {
                obj.link = customModule.link
                obj.type = 'link'
                customModules.push(obj)
              }
            })
            this.customModuleSpeedDialItems = customModules
          }
        }).finally(() => {
          this.setSpeedDialItems()
          this.getChatUnread()
          this.customModulesLoading = false
          this.fieldsLoading = false
        })
    },

    /**
     * Unread count for the floating chat launcher.
     *
     * Only called once the module list has told us chat is enabled here, so a
     * project without chat costs no extra request. Failures are swallowed —
     * both here and, via silenceErrors, in App.vue's global interceptor, which
     * would otherwise toast "Somthing went wrong" for a 403/500 on a badge the
     * launcher does not need. App.vue's notifier is what actually surfaces new
     * messages.
     */
    openChat () {
      this.chatDialog = true
    },

    /**
     * Closing refreshes the badge: messages read inside the dialog should not
     * leave a stale unread count on the launcher behind it.
     */
    closeChat () {
      this.chatDialog = false
      this.getChatUnread()
    },

    getChatUnread () {
      this.chatUnread = 0
      if (!this.projectFeaturePwa.project_chat) return

      hostAppApi.get(`${this.getHostRefApi}project/${this.activeProjectId}/chat/unread`, { silenceErrors: true })
        .then(response => {
          this.chatUnread = (response.data || {}).total || 0
        })
        .catch(() => { /* badge is optional */ })
    },
    windowResizeHandler () {
      this.clientHeight = window.innerHeight
      this.getSubMenuItemRightPosition()
    },
    getSubMenuItemRightPosition () {
      if (window.innerWidth > 1264) {
        this.subMenuRightPosition = this.pixelToVW((window.innerWidth - 1264))
      } else this.subMenuRightPosition = 1
    },
    pixelToVW (value) {
      return ((51 * value) / window.innerWidth).toFixed(2)
    }
  },
  components: {
    'form-data': FormTemplate,
    // Lazy: the chat view pulls in its own chunk, and most visits to a project
    // page never open it.
    'project-chat': () => import('./Chat.vue')
  },
  async beforeRouteUpdate (to, from, next) {
    if (to.params.project_id !== from.params.project_id) {
      try {
        this.fieldsLoading = true
        await store.dispatch('project/getProject', to.params.project_id)
      } catch (e) {
        console.error('Error fetching project:', e)
      } finally {
        this.fieldsLoading = false
      }
    }
    next()
  },
  beforeDestroy () {
    this.$eventBus.$off('languageChanged')
  },
  destroyed () {
    window.removeEventListener('resize', this.windowResizeHandler)
  }
}
</script>

<style>
.custom-speed-dial div.v-speed-dial__list {
  align-items: baseline !important;
}

.v-speed-dial--bottom {
  bottom: 10vh !important;
}

.custom_menu_position {
  position: fixed;
  bottom: 10vh !important;
}

@media screen and (min-height:300px) and (max-height:349px) and (orientation:landscape) {
  .custom_menu_position {
    bottom: 20vh !important;
  }
}

@media screen and (min-height:350px) and (max-height:399px) and (orientation:landscape) {
  .custom_menu_position {
    bottom: 18vh !important;
  }
}

@media screen and (min-height:400px) and (max-height:449px) and (orientation:landscape) {
  .custom_menu_position {
    bottom: 16vh !important;
  }
}

@media screen and (min-height:450px) and (max-height:499px) and (orientation:landscape) {
  .custom_menu_position {
    bottom: 14vh !important;
  }
}

@media screen and (min-height:500px) and (max-height:569px) and (orientation:landscape) {
  .custom_menu_position {
    bottom: 12vh !important;
  }
}

/* .custom_bottom_up {
  bottom: 10vh !important;
} */
</style>

<!-- 4975: scoped on purpose. The block above is global (it targets Vuetify's
     speed-dial internals); the launcher must not leak styles into other views. -->
<style scoped>
/* ---- Chat dialog ------------------------------------------------------- */
/* The chat shell inside is a flex column that needs a real height to size its
   scrolling stream; a v-card sized only by its content would collapse it. */
.pcc-dialog-card {
  display: flex;
  flex-direction: column;
  height: 80vh;
  max-height: 80vh;
}

.pcc-dialog-body {
  flex: 1;
  min-height: 0;
  overflow: hidden !important;
}

/* Fullscreen on phones: use the whole viewport instead of 80%. */
@media screen and (max-width: 960px) {
  .pcc-dialog-card {
    height: 100%;
    max-height: none;
  }
}

/* Floating chat launcher, matching the web app's bottom-right FAB.
   Sits clear of the app's bottom navigation (56px) plus a comfortable gap, and
   above the iOS safe-area inset so the home indicator cannot sit on it. */
.pcc-fab {
  position: fixed;
  right: 24px;
  bottom: calc(88px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
}

/* The app's content is a 1262px column centred in the viewport (see
   bottomNavigationLeftValue in Layout.vue, which centres the bottom nav the
   same way). A viewport-anchored FAB therefore drifts far to the right of the
   content on a wide screen. Pin it to the CONTENT's right edge instead:
   half the leftover gutter, plus the same 24px inset. Pure CSS, so unlike the
   bottom nav it needs no resize listener to stay put. */
@media screen and (min-width: 1262px) {
  .pcc-fab {
    right: calc((100vw - 1262px) / 2 + 24px);
  }
}

/* Landscape phones have very little vertical room; tuck it in tighter so it
   does not cover the module tiles. */
@media screen and (max-height: 500px) and (orientation: landscape) {
  .pcc-fab {
    bottom: calc(64px + env(safe-area-inset-bottom, 0px));
  }
}
</style>
